// Global
import { ComponentPropsContext } from '@sitecore-jss/sitecore-jss-nextjs';
import type { GetStaticPaths, GetStaticProps, NextPage } from 'next';
// Lib
import type { SitecorePageProps } from 'lib/sitecore/page-props';
import { sitecorePagePropsFactory } from 'lib/sitecore/page-props-factory';
import { sitemapFetcher } from 'lib/sitecore/sitemap-fetcher';
// Config
import { componentFactory } from 'temp/component-factory';
// Components
import { Core } from '.generated/templates/Foundation.Core.model';
import { Routes as Dataconstants } from '.generated/templates/Project.Routes.constants';
import { CartContextProvider } from 'components/globalContext/CartContext';
import { JoinCodeContextProvider } from 'components/globalContext/JoinCodeContext';
import { Auth } from 'components/helpers/Auth';
import NotFound from 'components/layout/NotFound';
import PageLayout from 'components/layout/PageLayout';
import { FastSitecoreContext } from 'lib/sitecore/fast-sitecore-context';
import { DictionaryContext } from 'lib/sitecore/use-dictionary';
import { getWithoutCase, isTruthy } from 'lib/util/urls';
import { DateTime } from 'luxon';
import { getServerState } from 'react-instantsearch-hooks-server';
import { InstantSearchServerState } from 'react-instantsearch-hooks-web';
import Impersonate from 'components/helpers/Auth/impersonate';
import { logWarning } from 'lib/util/errors';
import { env } from 'lib/env';
import { getSearchSortContext } from 'components/helpers/AlgoliaSearch/defaultSortBy';

const skipAuthentication = process.env.SKIP_AUTHENTICATION === 'true';
const anonymousRole = (process.env.ANONYMOUS_ROLE ?? '').trim();

type CreatedSitecorePageProps = Awaited<ReturnType<typeof sitecorePagePropsFactory.create>>;

type PageQueryValues = {
  query: URLSearchParams;
  joinCode?: string;
  naspreview?: string;
  txSearchIds?: [number, number];
};

const SitecorePage: NextPage<SitecorePageProps> = ({
  algoliaServerState, // NOSONAR
  isSearchRobot,
  componentProps,
  layoutData,
  notFound,
  dictionary,
  session,
  joinCode,
}) => {
  // NotFound should be caught/served as part of getStaticPaths but this is
  // here as a backup just in case.
  if (notFound || !layoutData?.sitecore?.route) {
    return <NotFound />;
  }
  return (
    <div id="outOfContext">
      <ComponentPropsContext value={componentProps}>
        <div id="inComponentContext">
          <FastSitecoreContext layoutData={layoutData} componentFactory={componentFactory}>
            <div id="inFastContext">
              <JoinCodeContextProvider joinCode={joinCode}>
                <div id="inJoinContext">
                  <DictionaryContext.Provider value={dictionary}>
                    <Auth session={session} skipAuthCheck={skipAuthentication}>
                      <div id="inAuth">
                        <CartContextProvider>
                          <div>
                            <Impersonate />
                          </div>
                          <div id="wrapPageLayout">
                            <PageLayout
                              algoliaServerState={algoliaServerState}
                              isSearchRobot={isSearchRobot === true}
                            />
                          </div>
                        </CartContextProvider>
                      </div>
                    </Auth>
                  </DictionaryContext.Provider>
                </div>
              </JoinCodeContextProvider>
            </div>
          </FastSitecoreContext>
        </div>
      </ComponentPropsContext>
    </div>
  );
};

export const getStaticPaths: GetStaticPaths = async (context) => {
  // Seeing some 502 Gateway errors with SSG that don't appear with ISR.
  // Essentially the SSG build overwhelms the back-end server, resulting in timeouts.
  // Since we use on-demand ISR, we don't get a lot from SSG anyways.
  const useSSG = false;

  if (useSSG) {
    const paths = await sitemapFetcher.fetch(context);

    return {
      paths: paths,
      fallback: true, // process.env.EXPORT_MODE ? false : 'blocking',
    };
  }

  return {
    paths: [],
    fallback: 'blocking',
  };
};

const userHasAccess = (
  roleString: string,
  knownOktaRoles: string[],
  allowedOktaRoleSets: string[]
) => {
  // Filter user roles so only sitecore-mapped ones are processed
  knownOktaRoles.push('sitecore-cm');
  const splitRoles = roleString
    .split('|')
    .map((r) => (knownOktaRoles.indexOf(r) === -1 ? null : r))
    .filter((r) => r !== null);
  splitRoles.sort((a, b) => (a ?? '').localeCompare(b ?? ''));

  const roleSet = splitRoles.join('|');

  // Note: We have to check role combinations (hence role set) because it's possible for say Role A to have access but Role B deny access.
  // We also can't just check if all roles individually have access because Role A can implicitly have no access
  // (neither granted nor denied) while Role B grants access, so the combined roles have access, where individually Role A does not.
  return roleSet === 'sitecore-cm' || allowedOktaRoleSets.indexOf(roleSet) >= 0;
};

//  Parse txSearchId query parameter into specific disease and stage identifiers.
const parseTxSearchIds = (input: string | null | undefined): [number, number] | undefined => {
  // Attempt to split out by underbar
  const splitIds = input?.trim()?.split('_');
  if (!splitIds || (splitIds.length ?? 0) < 2) {
    // Bad or missing input - no dice, return undefined.
    return undefined;
  }
  // Try to parse 2 numeric indentifiers...
  const diseaseId = Number.parseInt(splitIds[0], 10);
  const stageId = Number.parseInt(splitIds[1], 10);
  if (Number.isNaN(diseaseId) || Number.isNaN(stageId)) {
    // Bad or missing input - no dice, return undefined.
    return undefined;
  }
  // We made it this far we have enough to show the user.
  return [diseaseId, stageId];
};

const getPageQueryValues = (queryString: string): PageQueryValues => {
  const query = new URLSearchParams(queryString.replace(/^\?/, ''));
  const naspreview = query.get('naspreview') ?? undefined;

  query.delete('noquery');

  return {
    query,
    naspreview,
    joinCode:
      getWithoutCase(query, 'joinCode') || getWithoutCase(query, 'utm_term')?.split('-')?.at(1),
    txSearchIds: parseTxSearchIds(getWithoutCase(query, 'txId')),
  };
};

const applyQueryContext = (
  props: CreatedSitecorePageProps,
  queryValues: PageQueryValues
): CreatedSitecorePageProps => {
  if (!props.layoutData?.sitecore?.context || !queryValues.txSearchIds) {
    return props;
  }

  return {
    ...props,
    layoutData: {
      ...props.layoutData,
      sitecore: {
        ...props.layoutData.sitecore,
        context: {
          ...props.layoutData.sitecore.context,
          txSearchIds: queryValues.txSearchIds,
        },
      },
    },
  };
};

const isAuthEnabled = (): boolean => process.env.SKIP_AUTHENTICATION?.toString() !== 'true';

const getHasAccess = (
  props: CreatedSitecorePageProps,
  roleString: string,
  authEnabled: boolean
) => {
  const knownOktaRoles = props.layoutData?.sitecore?.context?.knownOktaRoles ?? [];
  const allowedOktaRoleSets = props.layoutData?.sitecore?.context?.allowedOktaRoles ?? [];

  return (
    props.notFound ||
    (authEnabled ? userHasAccess(roleString, knownOktaRoles, allowedOktaRoleSets) : true)
  );
};

const getRevalidate = (): number =>
  process.env.ISR_REVALIDATE_TIMER ? parseInt(process.env.ISR_REVALIDATE_TIMER, 10) : 60;

const getRedirectResponse = (
  props: CreatedSitecorePageProps,
  query: URLSearchParams,
  revalidate: number
) => {
  const routeData = props.layoutData?.sitecore
    ?.route as unknown as Core.BaseTemplates.Data.Fields.BaseRedirect;
  const contextRedirect = props.layoutData?.sitecore?.context?.redirect;
  const contextRedirectIsPermanent = props.layoutData?.sitecore?.context?.redirectIsPermanent;
  const linkField = routeData?.fields?.redirectUrl;
  const redirectHref = contextRedirect || linkField?.value.href;

  if (!redirectHref) {
    return undefined;
  }

  const newQueryString = query.toString();
  const destination = newQueryString ? `${redirectHref}?${newQueryString}` : redirectHref;
  const permanent = contextRedirect
    ? contextRedirectIsPermanent ?? false
    : routeData?.fields?.permanentRedirect.value;

  return {
    props: {},
    redirect: {
      destination,
      permanent,
    },
    revalidate,
  };
};

const shouldHideAnnouncement = (props: CreatedSitecorePageProps, naspreview?: string): boolean => {
  const pageTemplate = props.layoutData?.sitecore?.route?.templateId
    ?.replaceAll('-', '')
    .toLowerCase();
  const isAnnouncementPage =
    pageTemplate === Dataconstants.TemplateIds.Announcement.toLowerCase() ||
    pageTemplate === Dataconstants.TemplateIds.AnnouncementV2.toLowerCase();

  if (!isAnnouncementPage || isTruthy(naspreview)) {
    return false;
  }

  const publishDateValue = props.layoutData?.sitecore?.route?.fields?.PublishDate.value;
  const publishDate = publishDateValue
    ? DateTime.fromISO(publishDateValue.toString())
    : DateTime.fromISO('9999-01-01T00:00:00Z');

  return publishDate > DateTime.now();
};

const getUnauthorizedResponse = (
  props: CreatedSitecorePageProps,
  roleString: string,
  revalidate: number
) => {
  const encodeRequestUrl = encodeURIComponent(props.layoutData.sitecore.context.itemPath || '');
  const destination =
    anonymousRole === roleString
      ? `/auth/okta?callbackUrl=${encodeRequestUrl}&error=SessionRequired`
      : `/403?path=${encodeRequestUrl}`;

  return {
    props: {},
    redirect: {
      destination,
      permanent: false,
    },
    revalidate,
  };
};

const applySearchSortContext = (props: CreatedSitecorePageProps) => {
  const sitecoreContextData = props.layoutData?.sitecore?.context as
    | (Record<string, unknown> & { route?: unknown })
    | undefined;

  if (!sitecoreContextData) {
    return;
  }

  const searchSortContext = getSearchSortContext({
    ...sitecoreContextData,
    route: props.layoutData?.sitecore?.route,
  });

  sitecoreContextData.searchSortContext = {
    ...(searchSortContext.defaultSortByValue && {
      defaultSortByValue: searchSortContext.defaultSortByValue,
    }),
    ...(searchSortContext.defaultSortBySuffix && {
      defaultSortBySuffix: searchSortContext.defaultSortBySuffix,
    }),
    useTcDirectoryRouteState: searchSortContext.useTcDirectoryRouteState,
  };
};

const shouldSkipAlgoliaServerState = (props: CreatedSitecorePageProps): boolean =>
  !!props?.layoutData?.sitecore?.context?.pageEditing ||
  !!props?.notFound ||
  env.ALGOLIA_SERVER_RENDER_DISABLED ||
  !props?.layoutData?.sitecore?.route;

const loadAlgoliaServerState = async (
  props: CreatedSitecorePageProps
): Promise<InstantSearchServerState | null> => {
  if (shouldSkipAlgoliaServerState(props)) {
    return null;
  }

  try {
    return await getServerState(<SitecorePage {...props} />);
  } catch (e) {
    logWarning('Algolia server state failed to load', e);
    return null;
  }
};

export const getStaticProps: GetStaticProps = async (context) => {
  const { params } = context;
  let props = await sitecorePagePropsFactory.create(context);
  const roleString = (params?.role as string) ?? '';
  const queryString = (params?.customquery as string) || '';
  const queryValues = getPageQueryValues(queryString);
  const authEnabled = isAuthEnabled();

  props = applyQueryContext(props, queryValues);

  const hasAccess = getHasAccess(props, roleString, authEnabled);
  const revalidate = getRevalidate();

  if (hasAccess) {
    const redirectResponse = getRedirectResponse(props, queryValues.query, revalidate);
    if (redirectResponse) {
      return redirectResponse;
    }

    if (shouldHideAnnouncement(props, queryValues.naspreview)) {
      return {
        props: {},
        revalidate,
        notFound: true,
      };
    }
  }

  if (!hasAccess) {
    return getUnauthorizedResponse(props, roleString, revalidate);
  }

  applySearchSortContext(props);
  const algoliaServerState = await loadAlgoliaServerState(props);

  return {
    props: {
      ...props,
      isSearchRobot: authEnabled,
      algoliaServerState,
      ...(queryValues.joinCode && { joinCode: queryValues.joinCode }),
    },
    revalidate,
    notFound: props.notFound,
  };
};

export default SitecorePage;
